import React from 'react';
import { Collapse } from 'react-collapse';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { Translate } from '@bynder/localization';
import { Button, token } from '@bynder/design-system';
import { ModalType } from '../../../types';
import { getTypographyWeight } from '../../shared/styled-components/constants';
import { ROUTES } from '../../../constants/app';
import { useSidebarStore } from '../../../stores/sidebarStore';
import { useGuideStore } from '../../../stores/guideStore';
import { useModalStore } from '../../../stores/modalStore';
import { IconTOC } from '@bynder/icons';
import { addQuotes, toPx } from '../Inspector/helpers';

interface Props {
  isOpen: boolean;
  pageId: string;
}

const SidebarTableOfContents: React.FC<Props> = ({ isOpen, pageId }) => {
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const visibleSidebarElements = useSidebarStore(
    state => state.visibleSidebarElements,
  );
  const tableOfContents = useSidebarStore(s => s.tableOfContents);

  return (
    <StyledTableOfContents>
      <Collapse isOpened={isOpen}>
        {tableOfContents &&
          tableOfContents.map((item, index) => {
            if (item) {
              return (
                <TableOfContentsItem
                  isSelected={visibleSidebarElements[item.key] || false}
                  key={index}
                  entryKey={item.key}
                  pageId={pageId}
                  activeGuideId={activeGuideId!}
                >
                  {item.text}
                </TableOfContentsItem>
              );
            }
          })}
      </Collapse>
    </StyledTableOfContents>
  );
};

const StyledTableOfContents = styled.ul`
  padding-left: 7px;
`;

interface ToCItemProps {
  isSelected: boolean;
  pageId: string;
  activeGuideId: string;
  entryKey: string;
  children: React.ReactNode;
}
const TableOfContentsItem: React.FC<ToCItemProps> = ({
  children,
  isSelected,
  pageId,
  activeGuideId,
  entryKey,
}) => {
  const history = useHistory();

  return (
    <StyledTOCListItem
      onClick={() =>
        history.push({
          pathname: ROUTES.GUIDE_PAGE(activeGuideId, pageId),
          hash: entryKey,
        })
      }
      $isSelected={isSelected}
    >
      <p>{children}</p>
    </StyledTOCListItem>
  );
};

export const StyledTOCListItem = styled.div<{
  $isSelected?: boolean;
}>(({ $isSelected, theme }) => {
  const { themeset, getValueOrDefault } = theme;
  const { tableOfContents } = themeset?.sideBar || {};

  return `
    cursor: pointer;
    display: flex;
    align-items: center;
    background-color: ${
      $isSelected
        ? getValueOrDefault(
            'sideBar.tableOfContents.colors.background.active.hexCode',
          )
        : getValueOrDefault('sideBar.backgroundColor.hexCode')
    };
    overflow: hidden;
      padding-left: ${token.spacing4};
      border-left: ${
        $isSelected
          ? `solid ${getValueOrDefault(
              'sideBar.tableOfContents.colors.accent.active.hexCode',
            )} 2px`
          : `solid ${getValueOrDefault(
              'sideBar.tableOfContents.colors.accent.normal.hexCode',
            )} 2px`
      };
    margin-left: ${token.spacing5};
    p {
      font-family: ${addQuotes(tableOfContents?.fontFamily)};
      font-weight: ${getTypographyWeight(
        theme,
        tableOfContents?.fontFamily,
        tableOfContents?.fontStyle,
      )};
      padding-bottom: ${token.spacing3};
      padding-top: ${token.spacing3};
      color: ${
        $isSelected
          ? getValueOrDefault(
              'sideBar.tableOfContents.colors.text.active.hexCode',
            )
          : getValueOrDefault(
              'sideBar.tableOfContents.colors.text.normal.hexCode',
            )
      };
      font-size: ${toPx(getValueOrDefault('sideBar.tableOfContents.fontSize'))};
      line-height: 125%;
    }

    &:hover {
        background-color: ${getValueOrDefault(
          'sideBar.tableOfContents.colors.background.hover.hexCode',
        )};
        border-left: ${`solid ${getValueOrDefault(
          'sideBar.tableOfContents.colors.accent.hover.hexCode',
        )} 2px`};
        p {
          color: ${getValueOrDefault(
            'sideBar.tableOfContents.colors.text.hover.hexCode',
          )}
      }
  `;
});

interface PlaceholderProps {
  visibleInGuide: boolean;
  visibleInPage: boolean;
  handleSubmit: (visibleInGuide: boolean, visibleInPage: boolean) => void;
}
export const SidebarTableOfContentsPlaceholder: React.FC<PlaceholderProps> = ({
  visibleInGuide,
  visibleInPage,
  handleSubmit,
}) => {
  const openModal = useModalStore.use.openModal();
  const handleClick = () => {
    const modalProps = {
      visibleInGuide,
      visibleInPage,
    };
    openModal({
      modalType: ModalType.TABLE_OF_CONTENTS_MODAL,
      modalOnSubmit: handleSubmit,
      modalProps,
    });
  };

  return (
    <StyledTableOfContentsItemPlaceHolder>
      <Button
        variant="clean"
        icon={<IconTOC />}
        data-testid="tocMenu"
        onClick={handleClick}
      >
        <Translate id="SIDEBAR_TABLE_OF_CONTENTS.TABLE_OF_CONTENTS" />
      </Button>
    </StyledTableOfContentsItemPlaceHolder>
  );
};

const StyledTableOfContentsItemPlaceHolder = styled(StyledTOCListItem)`
  display: flex;
  justify-content: flex-start;
  button {
    &:hover {
      background-color: transparent;
    }
  }
`;

export default SidebarTableOfContents;
